import { Paper, Table, TableContainer, TablePagination } from "@mui/material";
import type { CellContext, ColumnDef, Row } from "@tanstack/react-table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import classNames from "classnames";
import { Pending } from "components/Management/ModalContent";
import type { ForwardedRef } from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import Pagination from "../shared/Pagination";
import DisplayCell from "./DisplayCell";
import EditableCell from "./EditableCell";
import { TableBody } from "./TableBody";
import TableHead from "./TableHead";
import type { TCell, TColumn, TTable } from "./types";

export interface EditableTableHandle<C extends TColumn> {
    getRows: () => Array<TTable<C[]>>; // Updated return type
}

interface Props<C extends TColumn> {
    columns: C[];
    rows: Array<TTable<C[]>>;
    onCellUpdate?: (o: { rowId: string | number; rowIndex: number; columnId: string; value: string | number }) => void;
    onCellBlur?: (rowIndex: number, columnKey: string, value: string) => void;
    renderRowActions?: (options: { row: Row<TTable<C[]>> }) => React.ReactNode;
    rowBackgroundColor?: (row: TTable<C[]>) => string | undefined;
    pagination?: {
        page: number;
        limit: number;
        total: number;
    };
    onPageChange?: (page: number) => void;
    onPageLimitChange?: (limit: number) => void;
    isLoading?: boolean;
}

const EditableTable = forwardRef(function EditableTable<C extends TColumn>(
    {
        columns,
        rows,
        onCellUpdate,
        onCellBlur,
        renderRowActions,
        rowBackgroundColor,
        pagination,
        onPageChange,
        onPageLimitChange,
        isLoading,
    }: Props<C>,
    ref: ForwardedRef<EditableTableHandle<C>>,
) {
    // Local state to track cell values for immediate UI updates
    const [localCellValues, setLocalCellValues] = useState<Record<string, Record<string, string>>>({});

    // Ref to track previous row data to detect meaningful changes
    const prevRowDataRef = useRef<string>();

    // Refs to avoid recreating functions on every render
    const onCellUpdateRef = useRef(onCellUpdate);
    const onCellBlurRef = useRef(onCellBlur);

    // Update refs when props change
    useEffect(() => {
        onCellUpdateRef.current = onCellUpdate;
        onCellBlurRef.current = onCellBlur;
    }, [onCellUpdate, onCellBlur]);

    const handleCellUpdate = useCallback(
        ({
            rowId,
            rowIndex,
            columnId,
            value,
        }: {
            rowId: string | number;
            rowIndex: number;
            columnId: string;
            value: string | number;
        }) => {
            console.log(`EditableTable handleCellUpdate - rowId: ${rowId}, columnId: ${columnId}, value: ${value}`);

            // Update local state immediately for UI responsiveness
            setLocalCellValues((prev) => {
                const newValues = {
                    ...prev,
                    [rowId]: {
                        ...prev[rowId],
                        [columnId]: String(value),
                    },
                };
                console.log("New localCellValues:", newValues);
                return newValues;
            });

            // Call parent callback
            onCellUpdateRef.current?.({ rowId, rowIndex, columnId, value });
        },
        [],
    );

    const handleCellBlur = useCallback((rowIndex: number, columnId: string, value: string) => {
        onCellBlurRef.current?.(rowIndex, columnId, value);
    }, []);

    const mainColumns = useMemo<ColumnDef<TTable<C[]>, TCell | undefined>[]>(() => {
        return columns
            .filter((o) => !o.isHidden)
            .map((col) => ({
                id: col.key, // Explicitly set the column ID to match the column key
                accessorFn: (row) => row.columns[col.key],
                header: col.label,
                enableResizing: false,
                size: col.size ?? 150,
                autoResetExpanded: false,
                autoResetPageIndex: false,
                autoResetRowState: false,
                cell: (props: CellContext<TTable<C[]>, TCell | undefined>) => {
                    const cellData = props.getValue();

                    if (cellData?.isEditable) {
                        return (
                            <EditableCell
                                {...props}
                                value={cellData?.value}
                                updateData={handleCellUpdate}
                                onBlur={handleCellBlur}
                                sx={cellData.sx}
                                className={cellData.className}
                                helperText={cellData.helperText}
                                localCellValues={localCellValues}
                            />
                        );
                    } else {
                        return (
                            <DisplayCell
                                value={cellData?.value}
                                sx={cellData?.sx}
                                className={cellData?.className}
                                helperText={cellData?.helperText}
                            />
                        );
                    }
                },
            })) as ColumnDef<TTable<C[]>, TCell | undefined>[];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, handleCellUpdate, handleCellBlur]);

    // Add 2 additional columns for action buttons and row numbers
    const allColumns = useMemo<ColumnDef<TTable<C[]>, any>[]>(() => {
        const columns: ColumnDef<TTable<C[]>, any>[] = [];

        if (renderRowActions) {
            columns.push({
                id: "actions",
                header: "",
                cell: ({ row }) => (renderRowActions ? renderRowActions({ row }) : null),
                size: 80,
                enableResizing: false,
            });
        }

        columns.push({
            id: "rowNumber",
            header: "",
            cell: ({ row }) => row.index + 1,
            size: 50,
            enableResizing: false,
        });

        columns.push(...(mainColumns as ColumnDef<TTable<C[]>, any>[]));
        return columns;
    }, [mainColumns, renderRowActions]);

    // Clear local values when rows change (new data from parent)
    // Only clear if the actual data content has changed, not just the reference
    useEffect(() => {
        // Create a stable key based on row IDs and values to detect real changes
        const rowDataKey = rows
            .map(
                (row) =>
                    `${row.id}:${Object.entries(row.columns)
                        .map(
                            ([key, cell]) =>
                                `${key}=${cell && typeof cell === "object" && "value" in cell ? cell.value : ""}`,
                        )
                        .join(",")}`,
            )
            .join("|");

        // Clear local values when data actually changes
        if (prevRowDataRef.current !== undefined && prevRowDataRef.current !== rowDataKey) {
            setLocalCellValues({});
        }
        prevRowDataRef.current = rowDataKey;
    }, [rows]);

    const table = useReactTable<TTable<C[]>>({
        data: rows,
        columns: allColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    useImperativeHandle(
        ref,
        () => ({
            getRows: (): Array<TTable<C[]>> => {
                const currentTableRows = table.getRowModel().rows;

                return currentTableRows.map((tableRow) => {
                    const outputRow: TTable<C[]> = {
                        id: tableRow.original.id,
                        columns: {},
                    };

                    (columns as C[]).forEach((colDef) => {
                        const columnKey = colDef.key;
                        // Get the cell object from the original row data
                        const cellObject = tableRow.original.columns[columnKey];

                        // Include local changes in the returned data
                        const rowId = tableRow.original.id;
                        const localValue = localCellValues[rowId]?.[columnKey];

                        if (localValue !== undefined && cellObject) {
                            outputRow.columns[columnKey] = {
                                ...cellObject,
                                value: localValue,
                            };
                        } else {
                            outputRow.columns[columnKey] = cellObject;
                        }
                    });
                    return outputRow;
                });
            },
        }),
        [table, columns, localCellValues],
    );

    return (
        <div className="relative size-full flex flex-col">
            <div className="flex-1 min-h-0!">
                {isLoading ? (
                    <div className="h-full flex flex-col items-center justify-center gap-2">
                        <Pending phrase="" />
                    </div>
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: "100%" }}>
                        <Table stickyHeader sx={{ tableLayout: "fixed" }}>
                            <TableHead headerGroups={table.getHeaderGroups()} />
                            <TableBody
                                rows={table.getRowModel().rows as any}
                                rowBackgroundColor={rowBackgroundColor as any}
                            />
                        </Table>
                    </TableContainer>
                )}
            </div>
            {pagination ? (
                <div
                    className={classNames("relative flex-none! [&_.pagination-container]:px-0!", {
                        "pointer-events-none": isLoading,
                        "opacity-70": isLoading,
                    })}
                >
                    <TablePagination
                        component={() => (
                            <Pagination
                                rowsPerPageOptions={[10, 25, 50]}
                                rowsPerPage={pagination.limit}
                                currentPageSize={Math.min(pagination.limit, pagination.total)}
                                page={pagination.page}
                                onPageChange={(_, newPage) => {
                                    if (isLoading) return;
                                    if (onPageChange) {
                                        onPageChange(newPage);
                                    }
                                }}
                                onRowsPerPageChange={(event) => {
                                    if (isLoading) return;

                                    const newLimit = +event.target.value;

                                    if (newLimit) {
                                        if (onPageLimitChange) {
                                            onPageLimitChange(newLimit);
                                        }
                                    }
                                }}
                                type="sales"
                                count={pagination.total}
                                sticky
                            />
                        )}
                        count={pagination.total}
                        rowsPerPage={pagination.limit}
                        page={pagination.page}
                        onPageChange={(_, newPage) => {
                            if (onPageChange) {
                                onPageChange(newPage);
                            }
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
});

export default EditableTable;
